import { Box } from '@mantine/core';
import { useEffect, useRef } from 'react';

type Dot = {
  x: number;
  y: number;
  s: number;
};

export function PortalDots() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const glowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const view = document.body as HTMLElement | null;
    if (!view) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const GAP = 26;
    const RADIUS = 140;

    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = 0;
    let height = 0;
    let dots: Dot[] = [];

    let raf = 0;
    let glowRaf = 0;
    let lastMove = -9999;
    let mx = -9999;
    let my = -9999;
    let gx = 0;
    let gy = 0;

    const moveGlow = () => {
      const glow = glowRef.current;
      if (glow) {
        glow.style.transform = `translate(${gx}px, ${gy}px)`;
      }
      glowRaf = 0;
    };

    const isDark = () =>
      document.documentElement.getAttribute('data-mantine-color-scheme') === 'dark';

    const build = () => {
      const rect = view.getBoundingClientRect();
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);

      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      dots = [];
      const cols = Math.ceil(width / GAP) + 1;
      const rows = Math.ceil(height / GAP) + 1;

      for (let j = 0; j < rows; j += 1) {
        for (let i = 0; i < cols; i += 1) {
          dots.push({ x: i * GAP, y: j * GAP, s: 0 });
        }
      }
    };

    const draw = () => {
      const dark = isDark();
      const baseAlpha = 0.14;

      ctx.clearRect(0, 0, width, height);

      let energy = false;
      for (const dot of dots) {
        const dx = dot.x - mx;
        const dy = dot.y - my;
        const dist = Math.hypot(dx, dy);
        const t = dist < RADIUS ? 1 - dist / RADIUS : 0;

        dot.s += (t - dot.s) * 0.16;
        if (dot.s > 0.003) {
          energy = true;
        }

        const lift = dot.s * 7;
        const radius = 1.2 + dot.s * 1.7;
        const alpha = baseAlpha + dot.s * (dark ? 0.5 : 0.42);

        ctx.beginPath();
        ctx.arc(dot.x, dot.y - lift, radius, 0, Math.PI * 2);

        if (dark) {
          ctx.fillStyle = `rgba(94, 215, 155, ${alpha})`;
        } else if (dot.s > 0.05) {
          ctx.fillStyle = `rgba(31, 199, 116, ${alpha})`;
        } else {
          ctx.fillStyle = `rgba(13, 40, 57, ${alpha})`;
        }

        ctx.fill();
      }

      if (!prefersReducedMotion.matches && (energy || performance.now() - lastMove < 600)) {
        raf = window.requestAnimationFrame(draw);
      } else {
        raf = 0;
      }
    };

    const kick = () => {
      if (!raf) {
        raf = window.requestAnimationFrame(draw);
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      const rect = view.getBoundingClientRect();
      mx = e.clientX - rect.left;
      my = e.clientY - rect.top;
      gx = mx;
      gy = my;
      lastMove = performance.now();

      if (!glowRaf) {
        glowRaf = window.requestAnimationFrame(moveGlow);
      }

      kick();
    };

    const onPointerLeave = () => {
      mx = -9999;
      my = -9999;
      lastMove = performance.now();
      kick();
    };

    const resizeObserver = new ResizeObserver(() => {
      build();
      kick();
    });

    resizeObserver.observe(view);
    view.addEventListener('pointermove', onPointerMove);
    view.addEventListener('pointerleave', onPointerLeave);

    build();
    draw();

    return () => {
      if (raf) {
        window.cancelAnimationFrame(raf);
      }

      if (glowRaf) {
        window.cancelAnimationFrame(glowRaf);
      }

      resizeObserver.disconnect();
      view.removeEventListener('pointermove', onPointerMove);
      view.removeEventListener('pointerleave', onPointerLeave);
    };
  }, []);

  return (
    <Box style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      <div
        data-testid="portal-glow"
        ref={glowRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 560,
          height: 560,
          margin: '-280px 0 0 -280px',
          borderRadius: '50%',
          pointerEvents: 'none',
          zIndex: 0,
          opacity: 0.9,
          background:
            'radial-gradient(circle, rgba(31, 199, 116, 0.11), rgba(31, 199, 116, 0.045) 42%, transparent 70%)',
          transform: 'translate(50vw, 40vh)',
          transition: 'transform .4s cubic-bezier(.22,.61,.36,1)',
        }}
        aria-hidden
      />
      <canvas
        data-testid="portal-dots"
        ref={canvasRef}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          width: '100%',
          height: '100%',
        }}
        aria-hidden
      />
    </Box>
  );
}
